"use client";

import {
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
  ComponentPropsWithoutRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Button, ButtonProps } from "../button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command/Command";
import { Popover, PopoverContent, PopoverTrigger } from "../popover/Popover";
import { Typography } from "../typography/Typography";
import { cn } from "../../utils";
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconX,
  IconSearch,
  IconSquare,
  IconSquareCheckFilled,
} from "@tabler/icons-react";
import { Spinner } from "../spinner/Spinner";
import DisplayContent from "./ComboboxDisplayContent";
import { DebouncedInput } from "../debounced-input/DebouncedInput";
import { InputGroup, InputGroupAddon, InputGroupButton } from "../input/InputGroup";

export interface ComboboxOption {
  value: string;
  label: string;
  labelNode?: ReactNode;
  children?: (close: () => void) => ReactNode;
  nestedLabels?: Record<string, string>;
  disabled?: boolean;
}

export interface ComboboxRef {
  close: () => void;
}

interface Props<TMultiSelect extends boolean = false> {
  options: ComboboxOption[];
  value?: TMultiSelect extends true ? string[] : string;
  onChange: (value: TMultiSelect extends true ? string[] : string) => void;
  placeholder?: string;
  header?: string;
  searchPlaceholder?: string;
  buttonProps?: ButtonProps;
  id?: string;
  icon?: ReactNode;
  multiSelect?: TMultiSelect;
  // if true, the button will only show the placeholder text instead of the selected values
  showOnlyPlaceholder?: boolean;
  showApplyButton?: boolean;
  showClearButton?: boolean;
  onLoadMore?: (searchValue?: string, page?: number) => Promise<void>;
  hasMore?: boolean;
  valueLabels?: Record<string, string>;
  onOpenChange?: (open: boolean) => void;
  popoverContentProps?: Omit<ComponentPropsWithoutRef<typeof PopoverContent>, "children">;
  popoverProps?: Omit<ComponentPropsWithoutRef<typeof Popover>, "children">;
  exactSearch?: boolean;
  // New props for FilterDropdown compatibility
  disableAllDeselect?: boolean;
  // Async loading props
  onPageReset?: () => void;
  // Hierarchical options support
  hoverDelayMs?: number;
  // New prop to enable search mode
  searchMode?: boolean;
}

// Delay before an async (server-backed) search refetch fires, so typing doesn't
// trigger one request per keystroke.
const SEARCH_DEBOUNCE_MS = 300;

export const ComboboxInner = <TMultiSelect extends boolean = false>(
  {
    options,
    value,
    onChange,
    placeholder,
    header,
    searchPlaceholder = "Search...",
    buttonProps,
    id,
    icon,
    multiSelect = false as TMultiSelect,
    showOnlyPlaceholder = false,
    showApplyButton = false,
    showClearButton = false,
    onLoadMore,
    hasMore = false,
    valueLabels,
    onOpenChange,
    popoverContentProps,
    popoverProps,
    exactSearch = false,
    disableAllDeselect = false,
    onPageReset,
    hoverDelayMs = 300,
    searchMode = false,
  }: Props<TMultiSelect>,
  ref: React.ForwardedRef<ComboboxRef>
) => {
  const [open, setOpen] = useState(popoverProps?.defaultOpen ?? false);
  const [isLoading, setIsLoading] = useState(false);
  const inflightRef = useRef(0);
  const [showAllDeselectWarning, setShowAllDeselectWarning] = useState(false);
  const [currentSearchValue, setCurrentSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Hierarchical support state
  const [selectedHierarchicalOption, setSelectedHierarchicalOption] =
    useState<ComboboxOption | null>(null);
  const [subPopoverOpen, setSubPopoverOpen] = useState(false);
  const [hoveringOption, setHoveringOption] = useState(false);
  const [hoveringSubPopover, setHoveringSubPopover] = useState(false);

  const commandListRef = useRef<HTMLDivElement | null>(null);
  const scrollCleanupRef = useRef<(() => void) | null>(null);
  const labelsCache = useRef<Map<string, string>>(new Map());
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs for scroll handler to avoid stale closures
  const hasMoreRef = useRef(hasMore);
  const isLoadingRef = useRef(isLoading);
  const currentPageRef = useRef(currentPage);
  const currentSearchValueRef = useRef(currentSearchValue);
  const onLoadMoreRef = useRef(onLoadMore);
  const subPopoverRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Debounces the async search fetch so server-backed options aren't refetched
  // on every keystroke (matches the debounced behavior of other searches).
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep refs in sync so the scroll handler always sees latest values
  useEffect(() => {
    hasMoreRef.current = hasMore;
    isLoadingRef.current = isLoading;
    currentPageRef.current = currentPage;
    currentSearchValueRef.current = currentSearchValue;
    onLoadMoreRef.current = onLoadMore;
  }, [hasMore, isLoading, currentPage, currentSearchValue, onLoadMore]);

  // Auto-detect hierarchical behavior based on subComponent presence
  const isHierarchical = options.some(option => option.children);

  // Initialize internal state based on external value
  const getInitialSelectedValues = useCallback((): string[] => {
    if (multiSelect) {
      return Array.isArray(value) ? value : value ? [value as string] : [];
    } else {
      return value ? [value as string] : [];
    }
  }, [value, multiSelect]);

  const [selectedValues, setSelectedValues] = useState<string[]>(getInitialSelectedValues());

  // Update labels cache when options change
  useEffect(() => {
    options.forEach(option => {
      labelsCache.current.set(option.value, option.label);

      // If this option has nested labels, cache them
      if (option.nestedLabels) {
        Object.entries(option.nestedLabels).forEach(([value, label]) => {
          labelsCache.current.set(value, label);
        });
      }
    });
  }, [options]);

  // Sync external value changes with internal state when not using apply button
  // or when the popover is closed (after apply has been pressed)
  useEffect(() => {
    if (!showApplyButton || !open) {
      setSelectedValues(getInitialSelectedValues());
    }
  }, [getInitialSelectedValues, showApplyButton, open]);

  // Callback ref for CommandList — attaches scroll listener when the element mounts
  const commandListCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up previous scroll listener
      if (scrollCleanupRef.current) {
        scrollCleanupRef.current();
        scrollCleanupRef.current = null;
      }

      commandListRef.current = node;

      if (!node || !onLoadMore) return;

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = node;

        if (!hasMoreRef.current || isLoadingRef.current || !onLoadMoreRef.current) return;

        if (scrollTop + clientHeight >= scrollHeight - 40) {
          inflightRef.current += 1;
          isLoadingRef.current = true;
          setIsLoading(true);
          const nextPage = currentPageRef.current + 1;
          onLoadMoreRef
            .current(currentSearchValueRef.current, nextPage)
            .then(() => {
              setCurrentPage(nextPage);
            })
            .finally(() => {
              inflightRef.current -= 1;
              if (inflightRef.current === 0) setIsLoading(false);
            });
        }
      };

      node.addEventListener("scroll", handleScroll);
      scrollCleanupRef.current = () => node.removeEventListener("scroll", handleScroll);
    },
    [onLoadMore]
  );

  // Cleanup when component closes
  useEffect(() => {
    if (open) {
      return;
    }
    // Cleanup scroll listener
    if (scrollCleanupRef.current) {
      scrollCleanupRef.current();
      scrollCleanupRef.current = null;
    }
    // Cleanup timeouts
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    setShowAllDeselectWarning(false);

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
      // The debounced fetch already set isLoading=true but was cancelled before
      // it ran (and before incrementing inflightRef), so no .finally will reset
      // it. Clear it here unless a real request is still in flight, otherwise
      // the spinner stays stuck and isLoadingRef wedges infinite scroll.
      if (inflightRef.current === 0) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [open]);

  // Load initial data when component mounts
  useEffect(() => {
    if (onLoadMore) {
      inflightRef.current += 1;
      setIsLoading(true);
      setCurrentPage(1);
      onLoadMore("", 1).finally(() => {
        inflightRef.current -= 1;
        if (inflightRef.current === 0) setIsLoading(false);
      });
    }
  }, [onLoadMore]); // Only depend on onLoadMore, not isLoading

  // Hierarchical hover management functions
  const handleOptionMouseEnter = useCallback(
    (option: ComboboxOption) => {
      if (!isHierarchical || !option.children) return;

      setHoveringOption(true);
      setSelectedHierarchicalOption(option);
      setSubPopoverOpen(true);

      // Clear any pending close timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    },
    [isHierarchical]
  );

  const handleOptionMouseLeave = useCallback(() => {
    if (!isHierarchical) return;

    setHoveringOption(false);

    // Only start the timeout if we're not already hovering the sub-popover
    if (!hoveringSubPopover) {
      // Clear any existing timeout to prevent race conditions
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      hoverTimeoutRef.current = setTimeout(() => {
        // Double-check we're still not hovering either element before closing
        if (!hoveringSubPopover && !hoveringOption) {
          setSubPopoverOpen(false);
          setSelectedHierarchicalOption(null);
        }
      }, hoverDelayMs);
    }
  }, [isHierarchical, hoveringSubPopover, hoveringOption, hoverDelayMs]);

  const handleSubPopoverMouseEnter = useCallback(() => {
    if (!isHierarchical) return;

    setHoveringSubPopover(true);
    // Clear any pending close timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, [isHierarchical]);

  const handleSubPopoverMouseLeave = useCallback(() => {
    if (!isHierarchical) return;

    setHoveringSubPopover(false);

    // Clear any existing timeout to prevent race conditions
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Only close if we're not hovering the main option
    if (!hoveringOption) {
      hoverTimeoutRef.current = setTimeout(() => {
        // Double-check we're still not hovering either element before closing
        if (!hoveringSubPopover && !hoveringOption) {
          setSubPopoverOpen(false);
          setSelectedHierarchicalOption(null);
        }
      }, hoverDelayMs);
    }
  }, [isHierarchical, hoveringOption, hoveringSubPopover, hoverDelayMs]);

  // Helper function to get label for a value (checks valueLabels prop, then current options, then cache)
  const getLabelForValue = useCallback(
    (value: string): string => {
      // First check valueLabels prop
      if (valueLabels && valueLabels[value]) {
        return valueLabels[value];
      }

      // Then check current options
      const currentOption = options.find(opt => opt.value === value);
      if (currentOption) {
        return currentOption.label;
      }

      // Then check cache
      const cachedLabel = labelsCache.current.get(value);
      if (cachedLabel) {
        return cachedLabel;
      }

      return value;
    },
    [options, valueLabels]
  );

  const handleSelect = (selectedValue: string) => {
    // A disabled option is non-selectable — clicking it is a no-op.
    if (options.find(opt => opt.value === selectedValue)?.disabled) {
      return;
    }
    if (multiSelect) {
      // For multi-select, toggle the selection
      const newSelectedValues = selectedValues.includes(selectedValue)
        ? selectedValues.filter(val => val !== selectedValue)
        : [...selectedValues, selectedValue];

      setSelectedValues(newSelectedValues);

      // Only update parent if not using apply button
      if (!showApplyButton) {
        onChange(newSelectedValues as TMultiSelect extends true ? string[] : string);
      }
    } else {
      // For single select, replace the selection and close
      onChange(selectedValue as TMultiSelect extends true ? string[] : string);
      handleOpenChange(false);
    }
  };

  const handleApply = () => {
    if (multiSelect) {
      // Check for disableAllDeselect validation
      if (disableAllDeselect && selectedValues.length === 0) {
        // Clear any existing timeout
        if (warningTimeoutRef.current) {
          clearTimeout(warningTimeoutRef.current);
        }

        // Show warning and set a new timeout
        setShowAllDeselectWarning(true);
        warningTimeoutRef.current = setTimeout(() => {
          setShowAllDeselectWarning(false);
          warningTimeoutRef.current = null;
        }, 5000);
        return;
      }

      // Clear timeout if the user applies valid selections
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }

      onChange(selectedValues as TMultiSelect extends true ? string[] : string);
    }
    handleOpenChange(false);
  };

  const handleClearSelection = () => {
    const emptySelection = multiSelect ? [] : "";
    setSelectedValues([]);

    // Only update parent immediately if not using apply button
    if (!showApplyButton) {
      onChange(emptySelection as TMultiSelect extends true ? string[] : string);
      handleOpenChange(false);
    }
  };

  const handleSearch = useCallback(
    (searchValue: string) => {
      setCurrentSearchValue(searchValue);

      // Reset pages when search changes (for async loading)
      if (searchValue !== currentSearchValue) {
        setCurrentPage(1);
        if (onPageReset) {
          onPageReset();
        }
        // Fetch filtered results from the API — debounced so a fast typist
        // doesn't fire one request per keystroke. The input value and local
        // (cmdk) filtering update immediately; only the network fetch waits.
        if (onLoadMore) {
          setIsLoading(true);
          if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
          }
          searchDebounceRef.current = setTimeout(() => {
            inflightRef.current += 1;
            onLoadMore(searchValue, 1)
              .catch(() => {})
              .finally(() => {
                inflightRef.current -= 1;
                if (inflightRef.current === 0) setIsLoading(false);
              });
          }, SEARCH_DEBOUNCE_MS);
        }
      }
    },
    [onPageReset, onLoadMore, currentSearchValue]
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      if (onOpenChange) {
        onOpenChange(newOpen);
      }
    },
    [onOpenChange]
  );

  // Expose close method via ref
  useImperativeHandle(
    ref,
    () => ({
      close: () => {
        handleOpenChange(false);
      },
    }),
    [handleOpenChange]
  );

  const renderTrigger = () => {
    if (searchMode && !multiSelect) {
      // In search mode, show a custom input with search icon and clear button
      // supports only single select mode
      const currentValue = getLabelForValue((value as string) || "");

      return (
        <div className="al-w-56">
          <InputGroup>
            <InputGroupAddon>
              <IconSearch className="al-size-4" />
            </InputGroupAddon>
            <DebouncedInput
              value={currentValue}
              placeholder={searchPlaceholder || "Search..."}
              debounceMs={300}
              className={buttonProps?.className}
              id={id ? `${id}-combobox` : "default-combobox"}
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              aria-controls={`${id}-combobox-content`}
              type="text"
              useInputGroup
            />
            {currentValue && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  size="icon"
                  variant="ghost"
                  onClick={e => {
                    e.stopPropagation();
                    onChange("" as TMultiSelect extends true ? string[] : string);
                  }}
                  aria-label="Clear input"
                >
                  <IconX className="al-size-4" />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
        </div>
      );
    }

    return (
      <Button
        variant="outline"
        role="combobox"
        aria-label={placeholder}
        aria-expanded={open}
        id={id ? `${id}-combobox` : "default-combobox"}
        {...buttonProps}
        className={cn(
          "al-w-56 al-flex al-items-center al-justify-between al-py-3 al-px-2",
          buttonProps?.className
        )}
      >
        {icon && <div className="al-flex-shrink-0">{icon}</div>}
        <div className="al-flex-1 al-min-w-0 al-text-left">
          <DisplayContent
            multiSelect={multiSelect}
            showOnlyPlaceholder={showOnlyPlaceholder}
            placeholder={placeholder}
            value={value}
            selectedValues={selectedValues}
            showApplyButton={showApplyButton}
            getLabelForValue={getLabelForValue}
          />
        </div>
        {popoverContentProps?.side === "right" ? (
          <IconChevronRight className="al-ml-2 al-size-4 al-shrink-0 al-opacity-50" />
        ) : (
          <IconChevronDown className="al-mr-2 al-size-4 al-shrink-0 al-opacity-50" />
        )}
      </Button>
    );
  };

  return (
    <Popover {...popoverProps} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
      <PopoverContent
        {...popoverContentProps}
        className={cn(
          "!al-p-0",
          "al-min-w-56",
          // to match width of trigger
          // https://github.com/shadcn-ui/ui/issues/3045#issuecomment-2773835617
          "!al-w-[var(--radix-popover-trigger-width)]",
          popoverContentProps?.className
        )}
        id={`${id}-combobox-content`}
        // https://github.com/radix-ui/primitives/issues/1159#issuecomment-1741282769
        // fix for combobox scrolling issue inside sheet component
        onWheel={e => {
          e.stopPropagation();
        }}
        sideOffset={searchMode ? -36 : 4}
        collisionPadding={searchMode ? { top: 40, bottom: 40 } : undefined}
      >
        <Command
          shouldFilter={!onLoadMore}
          filter={
            exactSearch
              ? (value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)
              : undefined
          }
        >
          {multiSelect && showApplyButton && (
            <div className="al-flex al-justify-between al-items-center al-p-2 al-border-b">
              <Typography>{header ?? placeholder}</Typography>
              <Button onClick={handleApply} size="sm">
                Apply
              </Button>
            </div>
          )}
          {showAllDeselectWarning && (
            <div className="al-flex al-items-center al-justify-center al-p-2 al-text-red-600 al-bg-red-50 al-border-b">
              <Typography className="al-text-center al-text-sm">
                At least one item must be selected.
              </Typography>
            </div>
          )}
          <CommandInput
            placeholder={searchPlaceholder}
            className="al-h-9"
            onValueChange={handleSearch}
          />
          <CommandList ref={commandListCallbackRef}>
            <CommandEmpty>
              {isLoading ? (
                <div className="al-flex al-items-center al-justify-center al-gap-2 al-text-sm al-text-muted-foreground al-py-2">
                  <Spinner size="sm" />
                  Loading...
                </div>
              ) : (
                "No matches found."
              )}
            </CommandEmpty>
            <CommandGroup className="al-overflow-auto">
              {options.map(option => (
                <div key={option.value} className="al-relative">
                  <CommandItem
                    value={option.label}
                    disabled={option.disabled}
                    aria-disabled={option.disabled}
                    onSelect={() =>
                      !isHierarchical || !option.children ? handleSelect(option.value) : undefined
                    }
                    aria-label={option.label}
                    className={cn("al-cursor-pointer", option.disabled && "al-cursor-not-allowed")}
                    title={option.label}
                    onMouseEnter={() => handleOptionMouseEnter(option)}
                    onMouseLeave={handleOptionMouseLeave}
                  >
                    <div className="al-flex al-items-center al-gap-2 al-w-full">
                      {multiSelect ? (
                        selectedValues.includes(option.value) ? (
                          <IconSquareCheckFilled
                            className="al-mr-2 al-size-4 al-text-primary"
                            role="checkbox"
                            aria-checked="true"
                          />
                        ) : (
                          <IconSquare
                            role="checkbox"
                            aria-checked="false"
                            className="al-mr-2 al-size-4 al-text-muted-foreground"
                          />
                        )
                      ) : (
                        <IconCheck
                          className={cn(
                            "al-mr-2 al-size-4",
                            (value as string) === option.value ? "al-opacity-100" : "al-opacity-0"
                          )}
                        />
                      )}
                      <div className="al-flex-1 al-min-w-0">
                        {option.labelNode ? (
                          option.labelNode
                        ) : (
                          <span className="al-text-sm al-leading-5 al-line-clamp-2 al-break-words">
                            {option.label}
                          </span>
                        )}
                      </div>
                      {isHierarchical && option.children && (
                        <IconChevronRight
                          className="al-ml-2 al-size-4 al-shrink-0 al-opacity-50"
                          role="img"
                        />
                      )}
                    </div>
                  </CommandItem>
                  {isHierarchical &&
                    selectedHierarchicalOption?.value === option.value &&
                    subPopoverOpen &&
                    option.children && (
                      <Popover open={subPopoverOpen}>
                        <PopoverTrigger asChild>
                          <div className="al-absolute al-top-0 al-left-0 al-w-full al-h-full al-pointer-events-none" />
                        </PopoverTrigger>
                        <PopoverContent
                          ref={subPopoverRef}
                          className="al-p-4 al-min-w-[300px] al-max-h-[90vh] al-overflow-auto"
                          align="start"
                          side="right"
                          sideOffset={8}
                          onMouseEnter={handleSubPopoverMouseEnter}
                          onMouseLeave={handleSubPopoverMouseLeave}
                          style={{
                            backgroundColor: "white",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                            border: "1px solid #e7e8ea",
                            borderRadius: "8px",
                            zIndex: 3000,
                          }}
                        >
                          {option.children(() => {
                            setSubPopoverOpen(false);
                            setSelectedHierarchicalOption(null);
                            handleOpenChange(false);
                          })}
                        </PopoverContent>
                      </Popover>
                    )}
                </div>
              ))}
              {onLoadMore && hasMore && options.length > 0 && (
                <div className="al-py-2 al-px-2 al-text-center" data-testid="load-more-indicator">
                  {isLoading && (
                    <div className="al-flex al-items-center al-justify-center al-gap-2 al-text-sm al-text-muted-foreground">
                      <div className="al-w-4 al-h-4 al-border-2 al-border-current al-border-t-transparent al-rounded-full al-animate-spin"></div>
                      Loading more...
                    </div>
                  )}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {showClearButton && (
            <div className="al-border-t al-p-2">
              <Button
                size="sm"
                variant="outline"
                className="!al-h-8 al-w-full"
                onClick={handleClearSelection}
              >
                Clear Selection
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const Combobox = forwardRef(ComboboxInner) as <TMultiSelect extends boolean = false>(
  props: Props<TMultiSelect> & { ref?: React.ForwardedRef<ComboboxRef> }
) => ReturnType<typeof ComboboxInner>;
